<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    header="Scan ID Barcode"
    :modal="true"
    :closable="true"
    :style="dialogStyle"
    :breakpoints="{ '960px': '95vw' }"
    @hide="onClose"
  >
    <div class="barcode-scanner-container">
      <!-- Session Warning -->
      <div
        v-if="!authStore.session.tenantId"
        class="flex flex-column align-items-center justify-content-center p-4"
      >
        <i class="pi pi-exclamation-triangle text-orange-500 text-4xl mb-3"></i>
        <p class="text-orange-600 font-semibold">Session not initialized</p>
        <p class="text-sm text-gray-600">Please wait for the application to finish loading...</p>
      </div>

      <!-- Scanner View -->
      <template v-else-if="!showScanResults && !showCreateDialog">
        <div v-if="useCamera" class="video-container">
          <video ref="videoElement" class="scanner-video"></video>
          <div class="scanner-overlay">
            <div class="scanner-target">
              <div class="scan-line"></div>
            </div>
          </div>
        </div>
        <div class="scanner-status mt-2">
          {{ uiStatus }}
        </div>
        <div v-if="useCamera" class="scanner-debug mt-2 text-xs text-gray-500">
          <div>📹 Scanner processes the ENTIRE video frame</div>
          <div>🎯 Target area is just a visual guide</div>
          <div>💡 Try different positions - center, bottom, edges</div>
          <div>📱 Mobile cameras work best at 6-12 inches distance</div>
        </div>
        <div class="flex gap-2 mt-3 justify-content-center">
          <Button
            label="Use Camera"
            icon="pi pi-camera"
            @click="startLocalCameraScan"
            :disabled="isScanning"
          />
          <Button
            v-if="hasRemoteIdScanner"
            label="Wait for USB Scan"
            icon="pi pi-id-card"
            class="p-button-outlined"
            @click="waitForUsbScan"
            :loading="waitingForUsb"
          />
        </div>
      </template>

      <!-- Results View (merged into the same dialog) -->
      <template v-else-if="showScanResults">
        <div class="w-full">
          <h3 class="text-lg font-semibold mb-3">Select Contact</h3>
          <p class="text-sm text-gray-600 mb-3">
            We found {{ matchingPersons.length }} potential match{{
              matchingPersons.length === 1 ? '' : 'es'
            }}. Select a contact or create a new one.
          </p>

          <!-- Suppliers Section -->
          <div v-if="suppliers.length > 0" class="mb-4">
            <h4 class="text-md font-semibold mb-2 flex align-items-center">
              <i class="pi pi-shopping-cart mr-2 text-green-600"></i>
              Suppliers ({{ suppliers.length }})
            </h4>
            <div class="grid">
              <div v-for="person in suppliers" :key="person.id" class="col-12 mb-2">
                <div
                  class="p-3 border-1 surface-border border-round flex align-items-center justify-content-between"
                >
                  <div class="flex flex-column">
                    <div class="font-semibold">
                      {{ person.firstName }} {{ person.lastName }}
                      <span class="text-xs text-gray-500">• {{ person.matchReason }}</span>
                    </div>
                    <div v-if="person.email" class="text-xs text-gray-600">
                      <i class="pi pi-envelope mr-1"></i>{{ person.email }}
                    </div>
                    <div v-if="person.phone" class="text-xs text-gray-600">
                      <i class="pi pi-phone mr-1"></i>{{ person.phone }}
                    </div>
                  </div>
                  <Button
                    label="Compare"
                    size="small"
                    icon="pi pi-eye"
                    @click="onPersonCompare(person)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- CRM Contacts Section -->
          <div v-if="crmContacts.length > 0" class="mb-4">
            <div class="flex align-items-center justify-content-between mb-2">
              <h4 class="text-md font-semibold flex align-items-center">
                <i class="pi pi-users mr-2 text-blue-600"></i>
                CRM Contacts ({{ crmContacts.length }})
              </h4>
              <Button
                :label="showCrmContacts ? 'Hide' : 'Show'"
                :icon="showCrmContacts ? 'pi pi-eye-slash' : 'pi pi-eye'"
                size="small"
                class="p-button-text"
                @click="showCrmContacts = !showCrmContacts"
              />
            </div>
            <div v-if="showCrmContacts" class="grid">
              <div v-for="person in crmContacts" :key="person.id" class="col-12 mb-2">
                <div
                  class="p-3 border-1 surface-border border-round flex align-items-center justify-content-between"
                >
                  <div class="flex flex-column">
                    <div class="font-semibold">
                      {{ person.firstName }} {{ person.lastName }}
                      <span class="text-xs text-gray-500">• {{ person.matchReason }}</span>
                    </div>
                    <div v-if="person.email" class="text-xs text-gray-600">
                      <i class="pi pi-envelope mr-1"></i>{{ person.email }}
                    </div>
                    <div v-if="person.phone" class="text-xs text-gray-600">
                      <i class="pi pi-phone mr-1"></i>{{ person.phone }}
                    </div>
                  </div>
                  <Button
                    label="Compare"
                    size="small"
                    icon="pi pi-eye"
                    @click="onPersonCompare(person)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="text-center mt-3">
            <Button
              label="Create New Contact"
              icon="pi pi-plus"
              class="p-button-outlined"
              @click="openCreateDialog()"
            />
          </div>
        </div>
      </template>

      <!-- New Supplier Dialog (No Match Flow) -->
      <NewSupplierDialog
        v-else-if="showCreateDialog"
        :visible="showCreateDialog"
        :scanned-data="scannedData || undefined"
        @update:visible="showCreateDialog = $event"
        @person:new="handleNewPerson"
        @document="handleNewDocument"
      />
    </div>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="onClose" />
    </template>
  </Dialog>

  <!-- Inline capture dialog when a document is missing front photo -->
  <IdCaptureDialog
    v-model:visible="showCaptureDialog"
    :person="capturePerson"
    :id-document="pendingDoc"
    @save="onCaptureSaved"
    @cancel="() => (showCaptureDialog = false)"
  />

  <!-- Person Association Dialog -->
  <PersonAssociationDialog
    v-model:visible="showAssociationDialog"
    :person="selectedPerson"
    :scanned-data="scannedData"
    @associate="onPersonAssociated"
    @update-person="onPersonUpdated"
    @cancel="onAssociationCancel"
  />
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import { useIdScanner, type ScannedIdData } from '@/composables/useIdScanner'
import { useAgentCommandService } from '@/services/agent/AgentCommandService'
import type { Agent } from '@/types/Agent'
import { useLocalSettingsStore } from '@/stores/localSettingsStore'
import { useIDDocumentStore } from '@/stores/idDocumentStore'
import { IDDocumentModel } from '@/models/IDDocumentModel'
import { createPerson } from '@/factories/personFactory'
import IdCaptureDialog from '@/components/common/IdCaptureDialog.vue'
import PersonAssociationDialog from '@/components/common/PersonAssociationDialog.vue'
import NewSupplierDialog from '@/components/common/NewSupplierDialog.vue'
import { useAuthStore } from '@/stores/authStore'
import { usePersonStore } from '@/stores/v2/person'
import type { IDDocument } from '@/types/IDDocument'
import type { Person } from '@/types/Person'

interface Props {
  visible: boolean
  personId?: string
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'document', doc: any): void
  (e: 'person:new', person: Person): void
  (e: 'person:select', person: Person): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const toast = useToast()

// Create a ref for the video element
const videoElement = ref<HTMLVideoElement>()

// Track if the dialog is supposed to be scanning
const shouldBeScanning = ref(false)

// Use the ID scanner composable
const idDocumentStore = useIDDocumentStore()
const personStore = usePersonStore()
const authStore = useAuthStore()
const showCaptureDialog = ref(false)
const showScanResults = ref(false)
const showCreateDialog = ref(false)
const showAssociationDialog = ref(false)
const pendingDoc = ref<IDDocument | null>(null)
const scannedData = ref<ScannedIdData | null>(null)
const matchingPersons = ref<(Person & { matchReason: string; isSupplier: boolean })[]>([])
const selectedPerson = ref<Person | null>(null)
const showCrmContacts = ref(false)
const capturePerson = ref<Person | null>(null)
const isNewPerson = ref(false)

const dialogStyle = computed(() =>
  showCreateDialog.value
    ? { width: '95vw', maxWidth: '95vw' }
    : { width: '90vw', maxWidth: '600px' },
)

// Handle scan results - shared logic for both camera and USB scanning
async function handleScanResult(data: ScannedIdData) {
  // Stop scanning immediately when we get a result
  shouldBeScanning.value = false
  stopScanning()

  // Store scanned data and find matching persons
  scannedData.value = data
  await findMatchingPersons(data)

  // Check for exact match by barcodeHash only
  const existingDoc = await checkForExactMatch(data)
  if (existingDoc) {
    console.log('[IdScanner] Found existing document:', {
      id: existingDoc.id,
      personId: existingDoc.personId,
      frontImageUrl: existingDoc.frontImageUrl,
      barcodeHash: existingDoc.barcodeHash,
    })
    if (existingDoc.frontImageUrl) {
      // If it already has an image, emit and close
      console.log('[IdScanner] Emitting document event with existingDoc:', existingDoc.id)
      emit('document', existingDoc)
      showScanResults.value = false
      emit('update:visible', false)
      return
    }
    // If image is missing, prompt capture for this document
    pendingDoc.value = existingDoc
    // Get the person associated with this document
    const person = await personStore.get(existingDoc.personId)
    capturePerson.value = person || null
    isNewPerson.value = false
    showScanResults.value = false
    showCaptureDialog.value = true
    // Don't close main dialog - let capture dialog show
    return
  }

  // Check if we have any matches
  if (matchingPersons.value.length === 0) {
    // No matches found - show full-screen create dialog instead of auto-creating
    console.log('[IdScanner] No matches found, opening full-screen create dialog')
    showScanResults.value = false
    showCreateDialog.value = true
    // Don't close main dialog - let create dialog show
    return
  }

  // Show scan results dialog for person association
  showScanResults.value = true
  // Don't close main dialog - let scan results dialog show
}

const {
  isScanning,
  scannerStatus,
  startScanning,
  stopScanning,
  cleanup,
  parseAAMVALicense,
  generateBarcodeHash,
} = useIdScanner({
  videoElement,
  onScan: handleScanResult,
  onError: (_error: string) => {
    // Swallow errors here; scanner UI shows status, parent expects only IDDocument emissions
  },
})

// Remote agent state
const agents = ref<Agent[]>([])
const localSettings = useLocalSettingsStore()
const hasRemoteIdScanner = ref(false)
const waitingForUsb = ref(false)
const uiStatus = ref('Initializing camera...')
const useCamera = ref(true)
const activeCommandId = ref<string | null>(null)
const wasCancelled = ref(false)

const selectedAgent = computed(() => {
  if (!localSettings.settings.selectedAgentId) return null
  return agents.value.find((a) => a.id === localSettings.settings.selectedAgentId) || null
})

import { useDevicesStore } from '@/stores/v2/devices'
const devicesStore = useDevicesStore()

const canUseAgentIdScan = computed(() => {
  // Enable if at least one scanner device is available from any agent
  return devicesStore.scanners.length > 0
})

// Computed properties for categorizing people
const suppliers = computed(() => {
  return matchingPersons.value.filter((person) => person.isSupplier)
})

const crmContacts = computed(() => {
  return matchingPersons.value.filter((person) => !person.isSupplier)
})

// Check for exact match by barcodeHash only
async function checkForExactMatch(data: ScannedIdData): Promise<IDDocument | null> {
  if (!data.barcodeHash) return null

  try {
    // Find ID documents with matching barcodeHash (regardless of image presence)
    const existingDocs = await idDocumentStore.query(
      [
        { field: 'barcodeHash', op: '==', value: data.barcodeHash },
        { field: 'isDeleted', op: '==', value: false },
      ],
      ['updatedAt', 'desc'],
      1,
    )

    if (existingDocs.length > 0) {
      return existingDocs[0]
    }
  } catch (error) {
    console.error('Error checking for exact match:', error)
  }

  return null
}

// Person matching logic
async function findMatchingPersons(data: ScannedIdData) {
  const matches: (Person & { matchReason: string; isSupplier: boolean })[] = []

  try {
    // TODO: Fix search method calls - there seems to be a TypeScript issue with the method signatures
    // The PersonSearch methods expect parameters but TypeScript is reporting they expect 0 arguments
    // This needs to be investigated and fixed properly

    // For now, use a simple approach that works
    const searchInstance = await personStore.search()
    const [barcodeMatches, nameMatches, idMatches] = await Promise.all([
      // Barcode hash search (most specific)
      data.barcodeHash
        ? (searchInstance as any).by2dBarcode(data.barcodeHash).run()
        : Promise.resolve([]),

      // Name search using normalized fields
      data.firstName || data.lastName
        ? (searchInstance as any).byExactName(data.firstName, data.lastName).run()
        : Promise.resolve([]),

      // ID number search
      data.idNumber ? (searchInstance as any).byIdNumber(data.idNumber).run() : Promise.resolve([]),
    ])

    // Add barcode matches
    for (const person of barcodeMatches) {
      const isSupplier = await checkIfSupplier(person.id)
      matches.push({ ...person, matchReason: 'Barcode Hash Match', isSupplier })
    }

    // Add name matches (avoid duplicates)
    for (const person of nameMatches) {
      if (!matches.find((m) => m.id === person.id)) {
        const isSupplier = await checkIfSupplier(person.id)
        matches.push({ ...person, matchReason: 'Name Match', isSupplier })
      }
    }

    // Add ID number matches (avoid duplicates)
    for (const person of idMatches) {
      if (!matches.find((m) => m.id === person.id)) {
        const isSupplier = await checkIfSupplier(person.id)
        matches.push({ ...person, matchReason: 'ID Number Match', isSupplier })
      }
    }

    matchingPersons.value = matches
  } catch (error) {
    console.error('Error finding matching persons:', error)
    matchingPersons.value = []
  }
}

// Helper function to check if a person is a supplier
async function checkIfSupplier(personId: string): Promise<boolean> {
  try {
    // This would need to be implemented based on your transaction logic
    // For now, return false as a placeholder
    return false
  } catch (error) {
    console.error('Error checking if person is supplier:', error)
    return false
  }
}

// Handle person comparison from scan results
function onPersonCompare(person: Person) {
  selectedPerson.value = person
  showAssociationDialog.value = true
  // Close the main scanner dialog to allow the association dialog to show
  emit('update:visible', false)
}

// Handle person selection from scan results
async function onPersonSelected(person: Person) {
  selectedPerson.value = person
  showAssociationDialog.value = true
  // Emit person:select event
  emit('person:select', person)
  // Close the main scanner dialog to allow the association dialog to show
  emit('update:visible', false)
}

function openCreateDialog() {
  showScanResults.value = false
  showCreateDialog.value = true
}

// Handle association dialog events
async function onPersonAssociated(person: Person) {
  // Create or update ID document with the selected person, will emit document
  if (!person.id) {
    console.error('onPersonAssociated: missing person.id')
    return
  }
  await createOrUpdateIdDocument(person.id)
}

async function onPersonUpdated(person: Person, scannedData: ScannedIdData) {
  // Update person with scanned data
  try {
    if (!person.id) {
      throw new Error('onPersonUpdated: missing person.id')
    }
    await personStore.update(person.id, {
      firstName: scannedData.firstName,
      lastName: scannedData.lastName,
      middleName: scannedData.middleName,
      idNumber: scannedData.idNumber,
      streetAddress: scannedData.address,
      city: scannedData.city,
      state: scannedData.state,
      postalCode: scannedData.postalCode,
      country: scannedData.country,
      dateOfBirth: scannedData.dateOfBirth,
      barcodeHash: scannedData.barcodeHash,
      updatedAt: new Date(),
    })

    // Then create or update ID document
    await createOrUpdateIdDocument(person.id)
  } catch (error) {
    console.error('Failed to update person:', error)
  }
}

function onAssociationCancel() {
  selectedPerson.value = null
  showAssociationDialog.value = false
  // Reopen the scanner dialog since user cancelled the association
  emit('update:visible', true)
}

// Handle creating a new person from scan results
async function onCreateNewPerson(data: ScannedIdData) {
  try {
    // Create new person from scanned data
    const newPerson = createPerson({
      firstName: data.firstName,
      lastName: data.lastName,
      middleName: data.middleName,
      idNumber: data.idNumber,
      streetAddress: data.address,
      city: data.city,
      state: data.state,
      postalCode: data.postalCode,
      country: data.country,
      dateOfBirth: data.dateOfBirth,
      barcodeHash: data.barcodeHash,
      type: 'supplier',
      status: 'supplier',
    })

    const createdPersonId = await personStore.create(newPerson)
    const createdPerson = await personStore.get(createdPersonId)

    if (!createdPerson) {
      throw new Error('Failed to create person')
    }

    // Create ID document for the new person (without emitting events yet)
    if (!createdPerson.id) {
      throw new Error('createOrUpdateIdDocumentForNewPerson: missing createdPerson.id')
    }
    await createOrUpdateIdDocumentForNewPerson(createdPerson.id)
  } catch (error) {
    console.error('Failed to create new person:', error)
  }
}

// Create or update ID document for a person
async function createOrUpdateIdDocument(personId: string) {
  if (!scannedData.value) return

  try {
    // Check if ID document already exists for this person and barcode hash
    const existingDocs = await idDocumentStore.query(
      [
        { field: 'personId', op: '==', value: personId },
        { field: 'barcodeHash', op: '==', value: scannedData.value.barcodeHash },
        { field: 'isDeleted', op: '==', value: false },
      ],
      ['updatedAt', 'desc'],
      1,
    )

    let doc: IDDocument

    if (existingDocs.length > 0) {
      // Update existing document
      doc = existingDocs[0]
      await idDocumentStore.update(doc.id!, {
        idNumber: scannedData.value.idNumber,
        issuer: scannedData.value.state,
        issueDate: scannedData.value.issueDate ? new Date(scannedData.value.issueDate) : undefined,
        expirationDate: scannedData.value.expirationDate
          ? new Date(scannedData.value.expirationDate)
          : undefined,
        rawBarcodeData: scannedData.value.rawData,
      })
    } else {
      // Create new document
      const newDoc = IDDocumentModel.create({
        personId,
        type: 'driver_license',
        idNumber: scannedData.value.idNumber,
        issuer: scannedData.value.state,
        issueDate: scannedData.value.issueDate ? new Date(scannedData.value.issueDate) : undefined,
        expirationDate: scannedData.value.expirationDate
          ? new Date(scannedData.value.expirationDate)
          : undefined,
        barcodeHash: scannedData.value.barcodeHash,
        rawBarcodeData: scannedData.value.rawData,
      })
      doc = await idDocumentStore.add(newDoc)
    }

    // Update person with currentIdDocumentId
    await personStore.update(personId, {
      currentIdDocumentId: doc.id,
      updatedAt: new Date(),
    })

    // Emit the document
    emit('document', doc)

    // If the document already has a front image, close the dialog
    if (doc.frontImageUrl) {
      showScanResults.value = false
      emit('update:visible', false)
      return
    }

    // If missing front image, prompt to capture within this dialog
    pendingDoc.value = doc
    // Get the person associated with this document
    const person = await personStore.get(personId)
    capturePerson.value = person || null
    isNewPerson.value = false
    showCaptureDialog.value = true
  } catch (error) {
    console.error('Failed to create/update ID document:', error)
  }
}

// Create ID document for a new person (without emitting events until photo capture)
async function createOrUpdateIdDocumentForNewPerson(personId: string) {
  if (!scannedData.value) return

  try {
    // Create new document
    const newDoc = IDDocumentModel.create({
      personId,
      type: 'driver_license',
      idNumber: scannedData.value.idNumber,
      issuer: scannedData.value.state,
      issueDate: scannedData.value.issueDate ? new Date(scannedData.value.issueDate) : undefined,
      expirationDate: scannedData.value.expirationDate
        ? new Date(scannedData.value.expirationDate)
        : undefined,
      barcodeHash: scannedData.value.barcodeHash,
      rawBarcodeData: scannedData.value.rawData,
    })
    const doc = await idDocumentStore.add(newDoc)

    // Update person with currentIdDocumentId
    await personStore.update(personId, {
      currentIdDocumentId: doc.id,
      updatedAt: new Date(),
    })

    // For new IDs, always show capture dialog (no front image yet)
    pendingDoc.value = doc
    // Get the person associated with this document
    const person = await personStore.get(personId)
    capturePerson.value = person || null
    isNewPerson.value = true
    showCaptureDialog.value = true
  } catch (error) {
    console.error('Failed to create ID document for new person:', error)
  }
}

// No-op placeholder retained for potential future UI actions

async function onCaptureSaved(frontUrl: string, backUrl?: string) {
  try {
    if (!pendingDoc.value?.id) return
    await idDocumentStore.update(pendingDoc.value.id, {
      frontImageUrl: frontUrl,
      backImageUrl: backUrl,
    })

    // Update person with currentIdDocumentId if not already set
    if (pendingDoc.value.personId) {
      await personStore.update(pendingDoc.value.personId, {
        currentIdDocumentId: pendingDoc.value.id,
        updatedAt: new Date(),
      })
    }

    const updated = await idDocumentStore.get(pendingDoc.value.id)
    if (updated) {
      // For new persons, emit both person:new and document events
      if (isNewPerson.value && capturePerson.value) {
        emit('person:new', capturePerson.value)
      }
      emit('document', updated)
    }
  } catch (e) {
    console.error('Failed updating IDDocument with captured images', e)
  } finally {
    showCaptureDialog.value = false
    pendingDoc.value = null
    capturePerson.value = null
    isNewPerson.value = false
    showScanResults.value = false
    emit('update:visible', false)
  }
}

// Watch for visibility changes
watch(
  () => props.visible,
  async (newValue) => {
    if (newValue) {
      shouldBeScanning.value = false
      useCamera.value = !canUseAgentIdScan.value
      if (canUseAgentIdScan.value) {
        uiStatus.value = 'Waiting for USB scan...'
        // kick off waiting immediately
        if (!waitingForUsb.value) waitForUsbScan()
      } else {
        uiStatus.value = 'No remote scanner detected. Use camera.'
      }
    } else {
      // Stop scanning when dialog is hidden
      shouldBeScanning.value = false
      stopScanning()
      // Cancel any active USB scan command
      cancelActiveCommand()
    }
  },
  { immediate: false },
)

// Cancel active USB scan command
async function cancelActiveCommand() {
  if (activeCommandId.value) {
    try {
      console.log('[IdScanner] Cancelling active command:', activeCommandId.value)
      wasCancelled.value = true
      // Update the command status to failed with cancellation reason
      const agentCommandService = useAgentCommandService(authStore.session)
      await agentCommandService.cancelCommand(selectedAgent.value?.id || '', activeCommandId.value)
      activeCommandId.value = null
    } catch (error) {
      console.error('[IdScanner] Failed to cancel command:', error)
    }
  }
}

// Handle close
function onClose() {
  shouldBeScanning.value = false
  stopScanning()
  // Cancel any active USB scan command
  cancelActiveCommand()
  showCreateDialog.value = false
  emit('update:visible', false)
}

// Handle new person from NewSupplierDialog
function handleNewPerson(person: Person) {
  console.log('[IdScanner] New person created:', person)
  emit('person:new', person)
}

// Handle new document from NewSupplierDialog
function handleNewDocument(document: IDDocument) {
  console.log('[IdScanner] New document created:', document)
  emit('document', document)
}

// Initialize on mount
onMounted(async () => {
  try {
    // TODO: Subscribe to agent presence from Realtime Database
    // For now, we'll need to implement this separately or use a different approach
    // agentService.onAgentsChange((list) => {
    //   agents.value = list
    //   const a = selectedAgent.value
    //   hasRemoteIdScanner.value = !!(
    //     a &&
    //     a.state === 'online' &&
    //     (a.caps || a.capabilities || []).includes('id.scan')
    //   )
    //   if (props.visible) {
    //     if (hasRemoteIdScanner.value) {
    //       uiStatus.value = 'Remote ID scanner available.'
    //     } else {
    //       uiStatus.value = 'No remote scanner detected. Use camera.'
    //     }
    //   } else {
    //     uiStatus.value = 'Ready to scan'
    //   }
    // })
  } catch (error) {
    console.error('Failed to initialize ID scanner:', error)
    uiStatus.value = 'Failed to initialize scanner'
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  cleanup()
})

// Start local camera scanning explicitly
async function startLocalCameraScan() {
  try {
    shouldBeScanning.value = true
    await nextTick()
    await startScanning()
    uiStatus.value = scannerStatus.value
  } catch (e) {
    console.error('Failed to start local camera scan', e)
  }
}

// Wait for a USB scan via remote agent with enhanced reliability
async function waitForUsbScan() {
  try {
    if (!devicesStore || devicesStore.scanners.length === 0) {
      uiStatus.value = 'No remote scanner detected. Use camera.'
      return
    }

    const scannerId = devicesStore.defaultScanner?.id || devicesStore.scanners[0]?.id
    if (!scannerId) {
      uiStatus.value = 'No remote scanner detected. Use camera.'
      return
    }

    uiStatus.value = 'Waiting for remote scanner…'
    const { text } = await devicesStore.scanId(scannerId)
    if (text && String(text).trim().length > 0) {
      uiStatus.value = 'Scan received'
      console.log('[IdScanner] Scan received:', text)

      const parsed = parseAAMVALicense(text)
      if (parsed) {
        console.log('[IdScanner] parsed AAMVA data', {
          firstName: parsed.firstName,
          lastName: parsed.lastName,
          idNumber: parsed.idNumber,
        })

        const hash = await generateBarcodeHash(text)
        parsed.barcodeHash = hash

        // Clear the active command ID since we got a successful result
        activeCommandId.value = null

        // Use the same scan result handler as the camera path
        await handleScanResult(parsed)

        toast.add({
          severity: 'success',
          summary: 'ID Scanned Successfully',
          detail: 'ID scanned via USB',
          life: 3000,
        })
        return
      }
    } else {
      uiStatus.value = 'Empty scan received'
    }
  } catch (e) {
    console.warn('[IdScanner] waitForUsbScan error:', (e as Error).message)
    uiStatus.value = 'Remote scan failed'
  }
}
</script>

<style scoped>
.barcode-scanner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.video-container {
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 300px;
  border: 2px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.scanner-target {
  width: 90%;
  height: 80px;
  border: 2px solid #fff;
  border-radius: 8px;
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 4000px rgba(0, 0, 0, 0.3);
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #00ff00;
  box-shadow: 0 0 8px 2px rgba(0, 255, 0, 0.8);
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% {
    top: 0;
  }
  50% {
    top: calc(100% - 2px);
  }
  100% {
    top: 0;
  }
}

.scanner-status {
  text-align: center;
  font-weight: 500;
  color: #666;
}

.cropper-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cropper {
  width: 100%;
  max-width: 500px;
  height: 300px;
}

.id-photo-container {
  width: 150px;
  height: 90px; /* 5:3 aspect ratio (150/90 = 5/3) */
  min-height: 90px;
}

.id-photo-container-full {
  width: 100%;
  /* 5:3 aspect ratio for full width */
  min-height: 200px;
}

.id-photo-container-half {
  width: 100%;
  min-height: 120px;
}

.id-photo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
